import { useParams } from 'react-router-dom';
import { BasePage } from '@/components/ui/base-page';
import { BreadcrumbNav, BreadcrumbItem } from '@/components/ui/breadcrumb-nav';
import { Play, ExternalLink, ThumbsUp, ThumbsDown, Share2, Bookmark, Calendar, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';

// Sample video data
const mockVideo = {
    name: 'blockchain-investing-guide',
    title: 'Blockchain Investing Guide: Beyond Cryptocurrencies',
    description: 'A comprehensive guide to investing in blockchain technology beyond just cryptocurrencies.',
    longDescription: 'This video explores the diverse investment opportunities within blockchain technology sectors, including infrastructure, enterprise applications, decentralized finance, and more. Our experts break down complex concepts into actionable investment strategies suitable for different risk profiles.',
    publishDate: 'June 15, 2023',
    duration: '18:24',
    views: '24,892',
    presenter: {
        name: 'Michael Rodriguez',
        title: 'Head of Technology Investment',
        image: 'https://randomuser.me/api/portraits/men/67.jpg'
    },
    thumbnailImage: 'https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=1000&q=80',
    youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    tags: ['Blockchain', 'Investing', 'Cryptocurrencies', 'Technology']
};

// Sample related videos
const relatedVideos = [
    {
        id: '1',
        title: 'Digital Asset Allocation Strategies',
        duration: '14:38',
        publishDate: 'May 23, 2023',
        views: '18,437',
        thumbnailImage: 'https://images.unsplash.com/photo-1622630998477-20aa696ecb05?auto=format&fit=crop&w=500&q=80',
        url: '/vid/digital-asset-allocation'
    },
    {
        id: '2',
        title: 'The Future of DeFi: Opportunities and Risks',
        duration: '22:15',
        publishDate: 'June 5, 2023',
        views: '31,205',
        thumbnailImage: 'https://images.unsplash.com/photo-1605792657660-596af9009e82?auto=format&fit=crop&w=500&q=80',
        url: '/vid/defi-opportunities-risks'
    },
    {
        id: '3',
        title: 'Navigating Crypto Market Volatility',
        duration: '16:42',
        publishDate: 'April 18, 2023',
        views: '42,891',
        thumbnailImage: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&w=500&q=80',
        url: '/vid/crypto-volatility'
    },
    {
        id: '4',
        title: 'Web3 Investment Landscape',
        duration: '19:56',
        publishDate: 'March 30, 2023',
        views: '15,782',
        thumbnailImage: 'https://images.unsplash.com/photo-1642033948077-5258142a97dd?auto=format&fit=crop&w=500&q=80',
        url: '/vid/web3-landscape'
    }
];

export function VideoPage() {
    const { name } = useParams<{ name: string }>();
    const [video, setVideo] = useState(mockVideo);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Simulate data fetching
        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    }, [name]);

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    const breadcrumbs: BreadcrumbItem[] = [
        { label: "Insights", href: "/insights" },
        { label: "Videos", href: "/insights/videos" },
        { label: video.title, href: `/vid/${name}` }
    ];

    return (
        <BasePage title={video.title} description={video.description}>
            <div className="container mt-6 mb-4">
                <BreadcrumbNav items={breadcrumbs} />
            </div>

            {/* Video player section */}
            <div className="bg-black">
                <div className="container mx-auto aspect-video relative max-h-[70vh]">
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Button
                            size="lg"
                            className="bg-red-600 hover:bg-red-700 text-white gap-2"
                            onClick={() => window.open(video.youtubeUrl, '_blank')}
                        >
                            <Play />
                            Watch on YouTube
                        </Button>
                    </div>
                    <img
                        src={video.thumbnailImage}
                        alt={video.title}
                        className="w-full h-full object-cover opacity-60"
                    />
                </div>
            </div>

            {/* Desktop layout (hidden on mobile) */}
            <div className="hidden md:block container py-8">
                <div className="grid grid-cols-3 gap-8">
                    {/* Left and middle columns */}
                    <div className="col-span-2 space-y-6">
                        <div>
                            <h1 className="text-3xl font-bold mb-2">{video.title}</h1>

                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <Calendar size={16} />
                                        {video.publishDate}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Clock size={16} />
                                        {video.duration}
                                    </span>
                                    <span>{video.views} views</span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="gap-1">
                                        <ThumbsUp size={16} /> Like
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1">
                                        <Share2 size={16} /> Share
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1">
                                        <Bookmark size={16} /> Save
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-muted p-4 rounded-lg">
                            <div className="flex items-start mb-4">
                                <div className="w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0">
                                    <img
                                        src={video.presenter.image}
                                        alt={video.presenter.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <h3 className="font-semibold">{video.presenter.name}</h3>
                                    <p className="text-sm text-muted-foreground">{video.presenter.title}</p>
                                </div>
                            </div>

                            <p className="text-sm mb-4">{video.longDescription}</p>

                            <div className="flex flex-wrap gap-2">
                                {video.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="px-3 py-1 bg-background rounded-full text-xs font-medium"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right column - Related videos */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">Related Videos</h2>
                        <div className="space-y-3">
                            {relatedVideos.map(item => (
                                <a href={item.url} key={item.id} className="group block">
                                    <div className="flex gap-3">
                                        <div className="relative w-32 h-20 flex-shrink-0 rounded overflow-hidden">
                                            <img
                                                src={item.thumbnailImage}
                                                alt={item.title}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                                                {item.duration}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">{item.title}</h3>
                                            <div className="flex flex-col gap-1 mt-1 text-xs text-muted-foreground">
                                                <span>{item.views} views</span>
                                                <span>{item.publishDate}</span>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile layout (hidden on desktop) */}
            <div className="md:hidden container py-6 space-y-6">
                <div>
                    <h1 className="text-xl font-bold mb-2">{video.title}</h1>
                    <div className="flex flex-wrap justify-between items-center text-sm text-muted-foreground mb-3">
                        <span>{video.views} views</span>
                        <span>{video.publishDate}</span>
                    </div>
                </div>

                <div className="flex justify-between border-y py-2">
                    <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1">
                        <ThumbsUp size={20} />
                        <span className="text-xs">Like</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1">
                        <ThumbsDown size={20} />
                        <span className="text-xs">Dislike</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1">
                        <Share2 size={20} />
                        <span className="text-xs">Share</span>
                    </Button>
                    <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1">
                        <Bookmark size={20} />
                        <span className="text-xs">Save</span>
                    </Button>
                </div>

                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                        <img
                            src={video.presenter.image}
                            alt={video.presenter.name}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-sm">{video.presenter.name}</h3>
                        <p className="text-xs text-muted-foreground">{video.presenter.title}</p>
                    </div>
                </div>

                <div className="bg-muted p-3 rounded-lg text-sm">
                    <p className="mb-3">{video.longDescription}</p>
                    <div className="flex flex-wrap gap-2">
                        {video.tags.map(tag => (
                            <span
                                key={tag}
                                className="px-2 py-1 bg-background rounded-full text-xs"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="font-semibold">More videos</h2>
                        <a href="/insights/videos" className="text-sm text-primary">See all</a>
                    </div>
                    <div className="space-y-3">
                        {relatedVideos.map(item => (
                            <a href={item.url} key={item.id} className="block">
                                <div className="flex gap-3">
                                    <div className="relative w-32 h-20 flex-shrink-0 rounded overflow-hidden">
                                        <img
                                            src={item.thumbnailImage}
                                            alt={item.title}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                                            {item.duration}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-sm line-clamp-2">{item.title}</h3>
                                        <div className="flex flex-col mt-1 text-xs text-muted-foreground">
                                            <span>{item.views} views</span>
                                            <span>{item.publishDate}</span>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </BasePage>
    );
} 